import { CurrencyPipe } from '@angular/common';
import { Injectable } from '@angular/core';
import { Dashboard, DashBoardApplication, OAOAppSummary } from '@app/modules/main/model/dashboard.model';
import { ApiService } from '@workflow/ecp-shared/service/api/api.service';
import { LenderService } from '@shared/service/lender/lender.service';
import { LoanApplicationStage } from '@shared/model/ApiResponses/ConsumerPortal/consumer-information.model';
import { LenderConfiguration } from '@shared/model/lender-config.model';
import { Observable } from 'rxjs';
import { LDRLoan } from 'src/postapp/consts/ldr-data.const';
import { AppSummary } from 'src/postapp/models/application.model';
import { LoanPurposeTypes, LoanType, LoansStatus } from 'src/shared/enum';
import { LoanService } from './loan.service';
import { SawDust } from '@cudirect/sawdust/sawdust';
import { ConsumerFunding } from 'src/postapp/models/consumer-funding.model';

@Injectable({
  providedIn: 'root'
})
export class ApplicationService {
  /** Lender Config */
  public lenderConfig: LenderConfiguration;

  // Commented cacheing code
  set applications(value: AppSummary[]) {
    if (value?.length) {
      this.loanService.cachedApp = value;
    }
  }

  get applications(): AppSummary[] {
    return this.loanService.cachedApp || [];
  }

  constructor(
    private readonly loanService: LoanService,
    private readonly lenderService: LenderService,
    private readonly cp: CurrencyPipe,
    private readonly apiService: ApiService,
    private readonly logger: SawDust
  ) {
    this.lenderConfig = this.lenderService.lenderDataInfo;
  }

  public getAllApplications(callback: (dashboardItems) => void, errCallback: (error) => void): void {
    /// loading dashboard from cache causing problem with updating dashboard on below example cases
    /// For example, if new loan added or a loan is submitted or a loan added in L360
    if (this.applications?.length !== 0) {
      callback(this.processResponse(this.applications));
      return;
    }
    this.getApps().subscribe(
      (result: AppSummary[]) => {
        // When Partial Save flag is turned OFF, appNumber coming as Null.
        // At this time, lets do not cache the data.
        if (result?.length) {
          this.applications = result.filter((f) => !f.appNumber).length ? undefined : result;
        }
        callback(this.processResponse(result));
      },
      (err) => {
        errCallback(err);
      }
    );
  }

  /**
   * getApplication
   */
  public getCachedAppById(applicationId: string): AppSummary {
    const app = this.applications.find((l) => l.appNumber === applicationId);
    if (app) {
      return app;
    }
    return undefined;
  }

  public updateAppToCache(appId: string, app: AppSummary): void {
    const apps = this.applications;
    const index = apps.findIndex((l) => l.appNumber === appId);
    if (index >= 0) {
      apps[index] = app;
      this.applications = apps;
    }
  }

  public invalidateCache(): void {
    this.loanService.cachedApp = [];
  }

  /**
   * Processes an array of application summaries and constructs a Dashboard object.
   *
   * This method serves to convert an array of AppSummary objects into a structured Dashboard
   * object. It segregates the applications based on their type ('Credit' and 'Membership') and
   * processes them accordingly.
   *
   * For 'Credit' type applications, each loan is processed using the processDashboardLoan method.
   * If the processed loan is successfully returned (not null), it is added to the dashboardLoans.loans array.
   * This ensures that only valid and error-free loans are considered for the dashboard.
   *
   * For 'Membership' type applications, each application is transformed into an OAOAppSummary object
   * with necessary properties assigned from the original application data. These are then added to the
   * dashboardLoans.apps array.
   *
   * @param result - An array of AppSummary objects representing loan and membership applications.
   * @returns A Dashboard object containing processed loans and membership applications, categorized
   *                      and structured for easy access and display.
   */
  private processResponse(result: AppSummary[]): Dashboard {
    const dashboardLoans = new Dashboard();
    if (!result?.length) return dashboardLoans;
    result
      .filter((l) => l.applicationType === 'Credit')
      .forEach((loan) => {
        const processedLoan = this.processDashboardLoan(loan); // Prevent corrupted loans from blowing everything up
        if (processedLoan) dashboardLoans.loans.push(processedLoan);
      });

    // TODO: Abstract mapping logic into a separate method
    dashboardLoans.apps = result
      .filter((r) => r.applicationType === 'Membership')
      .map((a) => {
        const app = {} as OAOAppSummary;
        app.membershipName = a.membershipTypeName;
        app.membershipStatus = a.membershipStatus === 'Submitted' ? a.loanStatus : a.membershipStatus;
        app.status = a.loanStatus;
        app.appNumber = a.appNumber;
        app.tempLoanNumber = a.tempAppNumber;
        app.applicants = a.applicants;
        app.isAppSubmitted = a.membershipStatus === 'Submitted' ? true : false;
        app.summary = a;
        return app;
      });
    return dashboardLoans;
  }

  private getApps(): Observable<AppSummary[]> {
    return this.apiService.request({
      urlObj: LDRLoan.GetAllLoans
    });
  }

  /**
   * Processes a loan application for the dashboard view.
   *
   * This method takes a data object representing a loan application and transforms it into a
   * DashBoardApplication object suitable for display on the dashboard. It involves:
   * - Assigning a formatted loan name, adding 'Loan' to the end if it's not already included.
   * - Casting the input data to the DashBoardApplication type.
   * - Calculating and assigning various properties of the application, such as amount,
   *   interest details, term, estimated monthly payment, and subject property address.
   * - Handling null or undefined values gracefully to avoid runtime errors.
   *
   * @param data - The loan application data object. Expected to have properties
   * like cardHeader, decision, requestedLoanAmount, applicationStage,
   * loanStatus, and subjectProperty.
   * @returns An object of type DashBoardApplication with processed and formatted data for dashboard display.
   */
  // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
  private processDashboardLoan(data): DashBoardApplication | null {
    try {
      const estMP = data.decision.estMonthlyPayment;
      let loanName: string = LoanPurposeTypes[data.cardHeader] ?? data.cardHeader;

      // Check for null or undefined before proceeding - see #
      if (loanName && !loanName.toLowerCase().includes('loan')) loanName += ' Loan';

      const app: DashBoardApplication = data as DashBoardApplication;
      app.amount = this.getAmount(data.decision?.amount, data.requestedLoanAmount, data.applicationStage, data.loanStatus);
      app.imageUrl = this.loanService.getCustomImageUrl(data);
      app.cardHeader = loanName;
      app.term = data.decision?.term ? `${data.decision.term} Months` : '';
      app.estMonthlyPayment = parseFloat(estMP) > 0 ? estMP : '';
      app.subjectPropertyAddress1 = this.getSubjectPropertyAddressLine1(data.subjectProperty);
      app.subjectPropertyAddress2 = this.getSubjectPropertyAddressLine2(data.subjectProperty);
      app.summary = data;

      return app;
    } catch (error) {
      this.logger.withError(error).error('Error processing dashboard loan');
      return null;
    }
  }

  private getPaymentDetails(data, olaSettings, term, rate, estMonthlyPayment, interestRateString) {
    let cardPaymentdetails = '';
    if (data.loanType === LoanType.CreditCard) {
      if (rate && olaSettings?.canShowRate) {
        return `${rate} ${interestRateString}`;
      }
      return '';
    } else {
      cardPaymentdetails += estMonthlyPayment && olaSettings?.canShowEstPayment && !data?.isRevolving ? `${estMonthlyPayment}/mo` : '';
      if (term && olaSettings?.canShowTerm && !data?.isRevolving) {
        const termMo = `${term} mo`;
        cardPaymentdetails += cardPaymentdetails ? ` for ${termMo}` : `${termMo}`;
      }
      if (rate && olaSettings?.canShowRate) {
        const interestRate = `${rate} ${interestRateString}`;
        cardPaymentdetails += cardPaymentdetails ? ` at ${interestRate}` : `${interestRate}`;
      }

      return cardPaymentdetails;
    }
  }

  private getSubjectPropertyAddressLine1(subjectProperty): string {
    const street = subjectProperty?.propertyAddress ? subjectProperty.propertyAddress : '';
    return `${street}`.trim();
  }

  private getSubjectPropertyAddressLine2(subjectProperty): string {
    const city = subjectProperty?.city ? subjectProperty.city + ', ' : '';
    const state = subjectProperty?.stateAbbreviation ? subjectProperty.stateAbbreviation + ' ' : '';
    const zip = subjectProperty?.zip ? subjectProperty.zip : '';
    return `${city}${state}${zip}`.trim();
  }

  private getAmount(loanAmount: string, reqLoanAmt: string, applicationStage: string, loansStatus: string): string {
    let amount = '';
    if (
      applicationStage === LoanApplicationStage.PostApplication &&
      loanAmount &&
      parseInt(loanAmount, 0) > 0 &&
      (loansStatus.toLowerCase() === LoansStatus.Approved || loansStatus.toLowerCase() === LoansStatus.Counter)
    ) {
      amount = loanAmount;
    } else if (reqLoanAmt && parseInt(reqLoanAmt, 0) > 0) {
      amount = reqLoanAmt;
    }
    return amount;
  }

  /** Save PreFunding & PreFunding Disbursement Data
   * @param consumerFunding
   * @returns Observable<any>
   */
  public savePreFundingData(consumerFunding: ConsumerFunding): Observable<any> {
    return this.loanService.submitDisburseFunding(consumerFunding);
  }

  /**
   * Save Credit Application Data
   * @param application
   * @param lenderLoanProductGUID
   * @returns Observable<any>
   */
  public saveCreditApplication(applicationId: string, lenderLoanProductGUID: string): Observable<boolean> {
    return this.loanService.saveCreditApplication(applicationId, lenderLoanProductGUID);
  }
  
}
